/**
 * "Coming to <County>" (docs/WAITLIST.md; core waitlist.ts). Where the 25 free days would start (StartTrialCard), a business in a county
 * that isn't open sees this instead: one email (their account's, editable), one UNTICKED box, one button. Nothing else in the app waits:
 * the checks, the binder, the library and /find/ all work. The server refuses Checkout too (409 county_not_open), so this card is the
 * explanation, not the lock.
 *
 * - useCountyHold(): null while loading, on error (the trial card shows; the server still guards), for homes and open counties.
 * - CountyHoldCard: the card. A "Not sure yet" county asks which county first; picking an open county offers to set it on the place.
 */
import { useCallback, useEffect, useState } from "react";
import { ALWAYS_WORKS, countyLabel, maskEmail, waitlistHoldWords } from "@logwave/core";
import type { WaitlistStatusResponse } from "../api.ts";
import { countiesFor } from "../lib/labels.ts";
import { useApi, useSession } from "../session.tsx";
import { Icon } from "./Icons.tsx";
import { AnchorButton, Button, ErrorBox, Field, LinkButton } from "./ui.tsx";

/** The business's county status for the plan, or null when nothing is held (or it couldn't be read: the server still guards). */
export function useCountyHold(enabled = true): { hold: WaitlistStatusResponse | null; loading: boolean; reload(): void } {
  const api = useApi();
  const s = useSession();
  const [status, setStatus] = useState<WaitlistStatusResponse | null | undefined>(enabled ? undefined : null);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) { setStatus(null); return; }
    let live = true;
    api.waitlistStatus(s.org.id).then((v) => { if (live) setStatus(v); }, () => { if (live) setStatus(null); });
    return () => { live = false; };
  }, [api, s.org.id, enabled, n]);
  const reload = useCallback(() => setN((x) => x + 1), []);
  return { hold: status && !status.open && !status.home ? status : null, loading: status === undefined, reload };
}

export function CountyHoldCard({ status, heading = true, onNotNow, onOpened }: { status: WaitlistStatusResponse; heading?: boolean; onNotNow?: () => void; /** The place's county was set to an open one: the trial card can show. */ onOpened?: () => void }) {
  const api = useApi();
  const s = useSession();
  const [email, setEmail] = useState(status.email);
  const [county, setCounty] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState<{ label: string; email: string } | null>(status.joined && status.countyLabel ? { label: status.countyLabel, email: status.email } : null);
  const [openHere, setOpenHere] = useState<{ county: string; label: string } | null>(null);
  const label = status.countyLabel ?? (county ? countyLabel(county) : null);
  const w = waitlistHoldWords(saved?.label ?? status.countyLabel, status.firstOpenLabel);
  const needsCounty = !status.county;

  const save = async () => {
    setBusy(true); setError(undefined);
    try {
      const r = await api.joinWaitlist(s.org.id, { consent, email: email.trim(), ...(needsCounty ? { county } : {}), page: "/billing/start" });
      if (r.joined) setSaved({ label: r.countyLabel, email: r.email });
      else setOpenHere({ county: r.county, label: r.countyLabel });
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setBusy(false);
  };
  const setPlaceCounty = async () => {
    if (!openHere || !status.locationId) return;
    setBusy(true); setError(undefined);
    try { await api.updateLocation(s.org.id, status.locationId, { county: openHere.county }); s.refresh(); onOpened?.(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); setBusy(false); }
  };

  const meanwhile = (
    <div className="stack" data-testid="hold-meanwhile">
      <p className="small">{w.meanwhile}</p>
      <div className="row">
        <LinkButton to="/learn/library" variant="secondary" icon="book">{w.library}</LinkButton>
        {status.findUrl && <AnchorButton href={status.findUrl} variant="secondary" icon="search">{w.find}</AnchorButton>}
      </div>
    </div>
  );

  if (openHere) {
    return (
      <div className="stack" data-testid="county-hold-open">
        {heading && <h2>{openHere.label} is open</h2>}
        <p className="lede">We're open in {openHere.label}. Set it as your kitchen's county, then start your free days.</p>
        <ErrorBox error={error} />
        <div className="form-actions">
          <Button big icon="check" busy={busy} onClick={() => void setPlaceCounty()}>Set {openHere.label}</Button>
          <Button variant="ghost" onClick={() => setOpenHere(null)}>Pick another county</Button>
        </div>
      </div>
    );
  }

  if (saved) {
    return (
      <div className="stack" data-testid="county-hold-saved">
        {heading && <h2>{w.title}</h2>}
        <p className="lede" role="status"><Icon name="check" size={20} /> {w.done(maskEmail(saved.email))}</p>
        {meanwhile}
        {onNotNow && <div className="form-actions"><Button variant="ghost" onClick={onNotNow}>Close this card</Button></div>}
      </div>
    );
  }

  return (
    <form className="stack" data-testid="county-hold" onSubmit={(e) => { e.preventDefault(); if (consent && (!needsCounty || county)) void save(); }}>
      {heading && <h2>{w.title}</h2>}
      <p className="lede">{w.lede}</p>
      <p>{w.body}</p>
      {needsCounty && (
        <Field label={w.pickCounty}>
          {(id) => <select id={id} value={county} onChange={(e) => setCounty(e.target.value)} data-testid="hold-county"><option value="">Pick your county</option>{countiesFor((status.state ?? "UT") as never).map((c) => <option key={c}>{c}</option>)}</select>}
        </Field>
      )}
      <Field label="Your email">
        {(id) => <input id={id} type="email" inputMode="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="hold-email" />}
      </Field>
      <label className="check">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} required data-testid="hold-consent" />
        {label ? `Email me when CleanCleaver opens in ${label}` : "Email me when CleanCleaver opens in my county"}
      </label>
      <ErrorBox error={error} />
      <div className="form-actions">
        <Button big type="submit" icon="check" busy={busy} disabled={!consent || (needsCounty && !county) || !email.trim()} reason={!consent ? "Tick the box first." : needsCounty && !county ? "Pick your county first." : undefined}>{w.action}</Button>
        {onNotNow && <Button variant="ghost" onClick={onNotNow}>Not now</Button>}
      </div>
      {meanwhile}
      <p className="caption">{ALWAYS_WORKS}</p>
    </form>
  );
}
